import { useEffect, useState } from 'react';
import { blogPosts as bundledPosts } from '../data/mock';

/**
 * The blog reads from two places: the articles bundled into the build, and the
 * articles SocialOS publishes to /api/socialos/posts, which the API serves
 * back at /api/blog-posts.
 *
 * Bundled posts win on a slug collision — an article shipped in the build is
 * the reviewed version, and an ingested post must never silently replace it.
 * The API is best-effort: if it is unreachable, the site renders exactly what
 * it rendered before this existed.
 */
export function useBlogPosts() {
  const [posts, setPosts] = useState(bundledPosts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch('/api/blog-posts?limit=100');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const remote = Array.isArray(data?.posts) ? data.posts : [];
        if (cancelled) return;

        const bundledSlugs = new Set(bundledPosts.map((p) => p.slug));
        const merged = [
          ...bundledPosts,
          ...remote.filter((p) => p?.slug && !bundledSlugs.has(p.slug)),
        ];

        merged.sort((a, b) => {
          const left = new Date(b.publishedDate || b.publishedAt || 0).getTime();
          const right = new Date(a.publishedDate || a.publishedAt || 0).getTime();
          return left - right;
        });

        setPosts(merged);
      } catch {
        // Keep the bundled posts — a blog that renders is better than an error.
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, loading };
}

export default useBlogPosts;
